import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';

import type { RoomPhoto } from '../types';

// Interface for key entries (can be moved to types.ts if used elsewhere)
export interface KeyEntry {
  handedOver: string;
  type: string;
  quantity: string;
  photos?: RoomPhoto[];
}

interface KeyEntryItemProps {
  keyEntry: KeyEntry;
  index: number;
  updateKeyEntry: (index: number, field: keyof KeyEntry, value: string) => void;
  removeKeyEntry: (index: number) => void;
}

const KeyEntryItem: React.FC<KeyEntryItemProps> = ({
  keyEntry,
  index,
  updateKeyEntry,
  removeKeyEntry
}) => {
  const [handedOver, setHandedOver] = useState(keyEntry.handedOver);
  const [type, setType] = useState(keyEntry.type);
  const [quantity, setQuantity] = useState(keyEntry.quantity);

  useEffect(() => {
    setHandedOver(keyEntry.handedOver);
    setType(keyEntry.type);
    setQuantity(keyEntry.quantity);
  }, [keyEntry]);

  useEffect(() => {
    const container = document.getElementById(`key-entry-${index}`);
    if (container) {
      const interactiveElements = container.querySelectorAll('input, textarea, select, button');
      interactiveElements.forEach(el => {
        (el as HTMLElement).style.touchAction = 'manipulation';
      });
    }
  }, [index]);

  return (
    <div
      id={`key-entry-${index}`}
      className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-4 items-center py-3 border-b border-gray-100 group hover:bg-gray-50"
      style={{ touchAction: 'pan-y' }}
    >
      <div className="sm:col-span-5">
        <label className="block text-sm font-medium text-gray-700 mb-1 sm:hidden">Keys Handed Over</label>
        <input
          type="text"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent text-base"
          value={handedOver}
          onChange={(e) => setHandedOver(e.target.value)}
          onBlur={() => updateKeyEntry(index, 'handedOver', handedOver)}
          placeholder="e.g., Front Door"
        />
      </div>
      <div className="sm:col-span-3">
        <label className="block text-sm font-medium text-gray-700 mb-1 sm:hidden">Type of Key</label>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent text-base"
          value={type}
          onChange={(e) => setType(e.target.value)}
          onBlur={() => updateKeyEntry(index, 'type', type)}
        >
          <option value="">Select Type</option>
          <option value="Yale">Yale</option>
          <option value="Mortice">Mortice</option>
          <option value="Fob">Fob</option>
          <option value="Card">Card</option>
          <option value="Other">Other</option>
        </select>
      </div>
      <div className="sm:col-span-3">
        <label className="block text-sm font-medium text-gray-700 mb-1 sm:hidden">Number of Keys</label>
        <input
          type="number"
          min="1"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent text-base"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          onBlur={() => updateKeyEntry(index, 'quantity', quantity)}
        />
      </div>
      <div className="sm:col-span-1 flex justify-end sm:justify-center">
        <button
          type="button"
          onClick={() => removeKeyEntry(index)}
          className="text-gray-400 hover:text-red-500 transition-colors duration-200"
          aria-label="Remove key"
        >
          <Trash2 className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default KeyEntryItem; 